import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { httpRequest } from "../redux/Action";
import { API_END_POINTS } from "../api/ApiEndPonts";
import {
  IoSearch,
  IoTrashOutline,
  IoCreateOutline,
  IoFilterOutline,
  IoListOutline,
  IoArrowBack,
} from "react-icons/io5";
import { motion } from "framer-motion";
import * as XLSX from "xlsx";
import { IoCloudUploadOutline } from "react-icons/io5";
function PreviousYearQuestion() {
  const dispatch = useDispatch();

  // --- States ---
  const [questions, setQuestions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentValue, setCurrentValue] = useState({
    question: "",
    options: ["", "", "", ""],
    correctAnswer: "",
    explanation: "",
    explanationImage: "",
    year: "",
    subject: "",
    exam: "",
  });
  const [editIdx, setEditIdx] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Filtering States
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [displayRange, setDisplayRange] = useState(20);
  const [page, setPage] = useState(1);
  const [openQuestionId, setOpenQuestionId] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // --- API Effects ---
  useEffect(() => {
    fetchQuestions();
  }, [page, displayRange, selectedYear]);

  const fetchQuestions = () => {
    dispatch(
      httpRequest({
        key: API_END_POINTS.getAllPYQ.KEY,
        params: {
          page: page,
          limit: displayRange,
          year: selectedYear || undefined,
        },
        onSuccess: (res) => {
          setQuestions(res.data || []);
        },
      }),
    );
  };

  const handleExcelChange = (e) => {
    setExcelFile(e.target.files[0]);
  };

  // --- Actions ---
  const toggleModal = (item = null) => {
    if (item) {
      setEditIdx(item._id);
      setCurrentValue({
        question: item.question || "",
        options: item.options || ["", "", "", ""],
        correctAnswer: item.correctAnswer || "",
        explanation: item.explanation || "",
        explanationImage: item.explanationImage || "",
        year: item.year || "",
        subject: item.subject || "",
        exam: item.exam || "",
      });
    } else {
      setEditIdx(null);
      setCurrentValue({
        question: "",
        options: ["", "", "", ""],
        correctAnswer: "",
        explanation: "",
        explanationImage: "",
        year: "",
        subject: "",
        exam: "",
      });
    }
    setIsModalOpen(true);
  };
  const toggleDropdown = (id) => {
    setOpenQuestionId(openQuestionId === id ? null : id);
  };

  const handleOptionChange = (value, index) => {
    const updatedOptions = [...currentValue.options];
    updatedOptions[index] = value;

    setCurrentValue({
      ...currentValue,
      options: updatedOptions,
    });
  };

  const saveQuestion = () => {
    if (!currentValue.question.trim()) return;

    const isEditing = editIdx !== null;

    dispatch(
      httpRequest({
        key: isEditing
          ? API_END_POINTS.updatePYQ.KEY
          : API_END_POINTS.createPYQ.KEY,
        params: isEditing ? { id: editIdx } : {},
        data: currentValue,
        onSuccess: () => {
          fetchQuestions();
          setIsModalOpen(false);
          setEditIdx(null);
        },
        onError: (err) => console.error("Save Error:", err),
      }),
    );
  };

  const removeQuestion = (id) => {
    if (!window.confirm("Are you sure you want to delete this paper?")) return;

    console.log("Deleting question with ID:", id);

    dispatch(
      httpRequest({
        key: API_END_POINTS.deletePYQ.KEY,
        params: { id },
        onSuccess: () => fetchQuestions(),
      }),
    );
  };

  const groupedByYear = questions.reduce((acc, item) => {
    const year = item.year || "Unknown";
    if (!acc[year]) acc[year] = [];
    acc[year].push(item);
    return acc;
  }, {});

  const startIndex = (page - 1) * displayRange;
  const endIndex = startIndex + displayRange;

  const totalItems = questions.filter((item) => {
    const matchesSearch = (item.question || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesYear =
      !selectedYear || String(item.year) === String(selectedYear);
    return matchesSearch && matchesYear;
  }).length;

  const totalPages = Math.ceil(totalItems / displayRange);

  const filteredData = questions
    .filter((item) => {
      const matchesSearch = (item.question || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesYear =
        !selectedYear || String(item.year) === String(selectedYear);
      return matchesSearch && matchesYear;
    })
    .slice(startIndex, endIndex);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedYear, displayRange]);

  const uploadExcelFile = () => {
    if (!excelFile) return alert("Please select Excel file");

    setUploading(true);

    const formData = new FormData();
    formData.append("file", excelFile);

    dispatch(
      httpRequest({
        key: API_END_POINTS.uploadPYQ.KEY, // 🔥 PYQ API
        data: formData,
        onSuccess: () => {
          alert("Excel Uploaded Successfully");
          setExcelFile(null);
          setUploading(false);
          fetchQuestions(); // refresh data
        },
        onError: () => {
          alert("Upload Failed");
          setUploading(false);
        },
      }),
    );
  };

 const downloadSampleExcel = () => {
  const headers = [
    "question",
    "option1",
    "option2",
    "option3",
    "option4",
    "correctAnswer",
    "year",
    "explanation",
    "explanationImage",
    "subject",
    "exam",
  ];

  const worksheet = XLSX.utils.aoa_to_sheet([headers]);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

  XLSX.writeFile(workbook, "PYQ_Template.xlsx");
};

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.headerArea}>
        <div style={styles.headerMain}>
          {/* LEFT SIDE */}
          <div>
            <div style={styles.badge}>Admin Panel</div>
            <h1 style={styles.title}>Rankers PYQ Database</h1>
            <p style={styles.subtitle}>
              Filter by year and manage test series questions line-by-line.
            </p>
          </div>

          {/* RIGHT SIDE BUTTON GROUP */}
          <div style={styles.buttonGroup}>
            {/* Upload Excel */}
            <label style={styles.addButton}>
              <IoCloudUploadOutline size={18} />
              Upload Excel
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={handleExcelChange}
                style={{ display: "none" }}
              />
            </label>

            {/* Submit */}
            {excelFile && (
              <button onClick={uploadExcelFile} style={styles.submitBtn}>
                {uploading ? "Uploading..." : "Submit"}
              </button>
            )}

            {/* Add Button */}
            <button onClick={() => toggleModal()} style={styles.addButton}>
              <span style={{ fontSize: "18px", fontWeight: "bold" }}>+</span>{" "}
              Add New Paper
            </button>
            <button onClick={downloadSampleExcel} style={styles.addButton}>
              Download Format
            </button>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div style={styles.toolbar}>
          <div style={styles.searchWrapper}>
            <IoSearch style={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search by title..."
              style={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div style={styles.filterGroup}>
            <div style={styles.dropdownBox}>
              <IoListOutline style={styles.iconInBox} />
              <select
                style={styles.select}
                value={displayRange}
                onChange={(e) => setDisplayRange(Number(e.target.value))}
              >
                <option value={5}>Show 5</option>
                <option value={10}>Show 10</option>
                <option value={20}>Show 20</option>
                <option value={50}>Show 50</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Line-by-Line List */}
      {!selectedYear ? (
        <div style={styles.cardContainer}>
          {Object.keys(groupedByYear)
            .sort((a, b) => b - a)
            .map((year) => (
              <div
                key={year}
                style={styles.card}
                onClick={() => setSelectedYear(year)}
              >
                <h2>{year}</h2>
                <p>{groupedByYear[year].length} Papers</p>
              </div>
            ))}
        </div>
      ) : (
        <>
          {/* 🔥 BACK BUTTON */}
          <button style={styles.backBtn} onClick={() => setSelectedYear(null)}>
            <IoArrowBack /> Back
          </button>

          {/* 🔥 QUESTIONS LIST */}
          <div style={styles.list}>
            {filteredData.map((item, index) => {
              const isOpen = openQuestionId === item._id;

              return (
                <div key={item._id} style={styles.rowContainer}>
                  {/* 🔥 TOP ROW */}
                  <div
                    style={styles.row}
                    onClick={() => toggleDropdown(item._id)}
                  >
                    <div>
                      <b>{index + 1}.</b> {item.question}
                      <span style={styles.year}>{item.year}</span>
                    </div>

                    <div onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => toggleModal(item)}>
                        <IoCreateOutline />
                      </button>
                      <button onClick={() => removeQuestion(item._id)}>
                        <IoTrashOutline />
                      </button>
                    </div>
                  </div>

                  {/* 🔥 DROPDOWN */}
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      style={styles.dropdown}
                    >
                      {/* Options */}
                      <div style={styles.optionList}>
                        {item.options?.map((opt, i) => (
                          <div
                            key={i}
                            style={{
                              ...styles.option,
                              backgroundColor:
                                opt === item.correctAnswer
                                  ? "#dcfce7"
                                  : "#f1f5f9",
                            }}
                          >
                            <b>{String.fromCharCode(65 + i)}.</b> {opt}
                          </div>
                        ))}
                      </div>

                      {/* Correct Answer */}
                      <div style={styles.correct}>
                        ✅ Correct: {item.correctAnswer}
                      </div>

                      {/* Explanation */}
                      <div style={styles.explanation}>
                        <b>Explanation:</b> {item.explanation}
                      </div>

                      {/* Image */}
                      {item.explanationImage && (
                        <img
                          src={item.explanationImage}
                          alt="explanation"
                          style={styles.image}
                        />
                      )}
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={styles.pagination}>
            <button
              onClick={() => setPage((prev) => prev - 1)}
              disabled={page === 1}
              style={styles.pageBtn}
            >
              ⬅ Prev
            </button>

            <span style={styles.pageInfo}>
              Page {page} of {totalPages}
            </span>

            <button
              onClick={() => setPage((prev) => prev + 1)}
              disabled={page === totalPages}
              style={styles.pageBtn}
            >
              Next ➡
            </button>
          </div>
        </>
      )}

      {/* Modal - Add/Edit */}
      {isModalOpen && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3>{editIdx !== null ? "Edit Question" : "Add New Question"}</h3>
              <button onClick={() => setIsModalOpen(false)}>✕</button>
            </div>

            <div style={styles.modalBody}>
              {/* Question */}
              <div style={styles.inputGroup}>
                <label>Question</label>
                <textarea
                  style={styles.input}
                  value={currentValue.question}
                  onChange={(e) =>
                    setCurrentValue({
                      ...currentValue,
                      question: e.target.value,
                    })
                  }
                />
              </div>

              {/* Options */}
              {currentValue.options.map((opt, i) => (
                <div key={i} style={styles.inputGroup}>
                  <label>Option {i + 1}</label>
                  <input
                    style={styles.input}
                    value={opt}
                    onChange={(e) => handleOptionChange(e.target.value, i)}
                  />
                </div>
              ))}

              {/* Correct Answer */}
              <div style={styles.inputGroup}>
                <label>Correct Answer</label>
                <select
                  style={styles.input}
                  value={currentValue.correctAnswer}
                  onChange={(e) =>
                    setCurrentValue({
                      ...currentValue,
                      correctAnswer: e.target.value,
                    })
                  }
                >
                  <option value="">Select Correct Option</option>
                  {currentValue.options.map((opt, i) => (
                    <option key={i} value={opt}>
                      Option {i + 1}
                    </option>
                  ))}
                </select>
              </div>

              {/* Explanation */}
              <div style={styles.inputGroup}>
                <label>Explanation</label>
                <textarea
                  style={styles.input}
                  value={currentValue.explanation}
                  onChange={(e) =>
                    setCurrentValue({
                      ...currentValue,
                      explanation: e.target.value,
                    })
                  }
                />
              </div>

              {/* Explanation Image */}
              <div style={styles.inputGroup}>
                <label>Explanation Image URL</label>
                <input
                  style={styles.input}
                  value={currentValue.explanationImage}
                  onChange={(e) =>
                    setCurrentValue({
                      ...currentValue,
                      explanationImage: e.target.value,
                    })
                  }
                />
              </div>

              {/* Year */}
              <div style={styles.inputGroup}>
                <label>Year</label>
                <input
                  type="number"
                  style={styles.input}
                  value={currentValue.year}
                  onChange={(e) =>
                    setCurrentValue({ ...currentValue, year: e.target.value })
                  }
                />
              </div>
              {/* subject */}
              <div style={styles.inputGroup}>
                <label>Subject</label>
                <input
                  type="text"
                  style={styles.input}
                  value={currentValue.subject}
                  onChange={(e) =>
                    setCurrentValue({ ...currentValue, subject: e.target.value })
                  }
                />
              </div>
              {/* Exam */}
              <div style={styles.inputGroup}>
                <label>Exam</label>
                <input
                  type="text"
                  style={styles.input}
                  value={currentValue.exam}
                  onChange={(e) =>
                    setCurrentValue({ ...currentValue, exam: e.target.value })
                  }
                />
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button onClick={saveQuestion}>
                {editIdx !== null ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
  
// --- Professional Admin Styles ---
const styles = {
  container: {
    padding: "10px 60px",
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  headerArea: {
    maxWidth: "1200px",
    margin: "0 auto 30px auto",
    backgroundColor: "#1e293b",
    padding: "30px",
    borderRadius: "16px",
    color: "#fff",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
  },
  headerMain: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "25px",
  },
  badge: {
    backgroundColor: "#38bdf8",
    color: "#082f49",
    padding: "4px 10px",
    borderRadius: "6px",
    fontSize: "11px",
    fontWeight: "bold",
    textTransform: "uppercase",
    marginBottom: "8px",
    display: "inline-block",
  },
  title: { fontSize: "28px", margin: 0, fontWeight: "700" },
  subtitle: { color: "#94a3b8", fontSize: "14px", marginTop: "5px" },
  addButton: {
    backgroundColor: "#38bdf8",
    color: "#082f49",
    border: "none",
    padding: "12px 20px",
    borderRadius: "10px",
    fontWeight: "700",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s",
  },
  toolbar: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  searchWrapper: {
    flex: 1,
    minWidth: "250px",
    display: "flex",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    borderRadius: "10px",
    padding: "0 15px",
    border: "1px solid rgba(255,255,255,0.1)",
  },
  searchIcon: { color: "#94a3b8" },
  searchInput: {
    width: "100%",
    padding: "12px",
    backgroundColor: "transparent",
    border: "none",
    color: "#fff",
    outline: "none",
  },
  filterGroup: { display: "flex", gap: "10px" },
  dropdownBox: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.08)", // Thoda aur subtle glass look
    padding: "0 10px",
    borderRadius: "12px", // Thoda rounded corners for modern feel
    border: "1px solid rgba(255, 255, 255, 0.15)",
    transition: "all 0.2s ease-in-out",
    cursor: "pointer",
    minWidth: "140px", // Takki text ke saath box chhota-bada na ho
  },

  // Isse use karein takki hover karne par box highlight ho
  dropdownBoxHover: {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderColor: "rgba(255, 255, 255, 0.3)",
  },

  select: {
    backgroundColor: "transparent",
    border: "none",
    color: "#000",
    padding: "12px 35px 12px 10px", // Proper padding for text and icon
    outline: "none",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    width: "100%",
    letterSpacing: "0.5px",
  },

  iconInBox: {
    color: "#38bdf8", // Blue icon for "Rankers" feel
    position: "absolute",
    right: "12px", // Arrow icon ko bilkul right mein fix karne ke liye
    pointerEvents: "none", // Icon click ko block na kare
  },

  listSection: {
    maxWidth: "1000px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    backgroundColor: "transparent",
    color: "#000",
  },
  row: {
    backgroundColor: "#fff",
    padding: "15px 25px",
    borderRadius: "12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: "1px solid #e2e8f0",
    transition: "transform 0.2s",
  },
  rowLeft: { display: "flex", alignItems: "center", gap: "20px" },
  rowNumber: {
    color: "#94a3b8",
    fontWeight: "bold",
    fontSize: "14px",
    width: "20px",
  },
  rowDetails: { display: "flex", flexDirection: "column", gap: "4px" },
  rowTitle: { fontWeight: "600", color: "#334155", fontSize: "16px" },
  rowYearBadge: {
    fontSize: "11px",
    color: "#0369a1",
    backgroundColor: "#f0f9ff",
    padding: "2px 8px",
    borderRadius: "4px",
    fontWeight: "700",
    width: "fit-content",
  },
  rowActions: { display: "flex", gap: "10px" },
  editAction: {
    backgroundColor: "#f1f5f9",
    color: "#475569",
    border: "none",
    padding: "8px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  deleteAction: {
    backgroundColor: "#fff1f2",
    color: "#e11d48",
    border: "none",
    padding: "8px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  emptyState: { textAlign: "center", padding: "50px", color: "#64748b" },
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(4px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    width: "100%",
    maxWidth: "700px",
    maxHeight: "90vh", // 🔥 scroll ke liye important
    overflowY: "auto", // 🔥 scroll enable
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    padding: "25px",
    boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #e2e8f0",
    paddingBottom: "10px",
  },
  closeX: {
    background: "none",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    color: "#64748b",
  },
  inputGroup: {
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "600",
    color: "#334155",
    fontSize: "14px",
  },
  input: {
    // width: "100%",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #cbd5f5",
    fontSize: "15px",
    boxSizing: "border-box",
    transition: "0.2s",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "10px",
    borderTop: "1px solid #e2e8f0",
    paddingTop: "15px",
  },
  btnCancel: {
    padding: "10px 16px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#e2e8f0",
    color: "#334155",
    cursor: "pointer",
    fontWeight: "600",
  },
  btnSave: {
    padding: "10px 18px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#0284c7",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "600",
  },
  cardContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px,1fr))",
    gap: 20,
  },

  card: {
    padding: 20,
    background: "#fff",
    borderRadius: 10,
    textAlign: "center",
    cursor: "pointer",
    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
  },

  backBtn: {
    marginBottom: 15,
    padding: "8px 12px",
    cursor: "pointer",
  },

  list: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  year: {
    marginLeft: 10,
    fontSize: 12,
    color: "gray",
  },

  modalBody: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    marginTop: "10px",
  },

  textarea: {
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #cbd5f5",
    fontSize: "14px",
    minHeight: "80px",
    resize: "vertical",
    outline: "none",
  },
  // 🔥 option input styling
  optionBox: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  optionLabel: {
    minWidth: "70px",
    fontWeight: "600",
    color: "#475569",
  },
  rowContainer: {
    borderRadius: "12px",
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    background: "#fff",
    cursor: "pointer",
  },

  dropdown: {
    padding: "15px 20px",
    background: "#f8fafc",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },

  optionList: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  option: {
    padding: "8px 12px",
    borderRadius: "8px",
    fontSize: "14px",
  },

  correct: {
    color: "#16a34a",
    fontWeight: "600",
  },

  explanation: {
    fontSize: "14px",
    color: "#334155",
  },

  image: {
    maxWidth: "200px",
    borderRadius: "10px",
    marginTop: "10px",
  },

  buttonGroup: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },

  uploadBtn: {
    backgroundColor: "#22c55e",
    color: "#fff",
    padding: "12px 16px",
    borderRadius: "10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontWeight: "600",
  },

  submitBtn: {
    backgroundColor: "#0ea5e9",
    color: "#fff",
    border: "none",
    padding: "12px 16px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "600",
  },
  pagination: {
    marginTop: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "15px",
  },

  pageBtn: {
    padding: "8px 16px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#1e293b",
    color: "#fff",
    cursor: "pointer",
  },

  pageInfo: {
    fontWeight: "600",
    color: "#334155",
  },
};

export default PreviousYearQuestion;
